import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  portal: localStorage.getItem('portal') || null,
  isAuthenticated: false,
  loading: true,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  UPDATE_USER: 'UPDATE_USER',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        portal: action.payload.portal || 'staff',
        isAuthenticated: true,
        loading: false,
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        portal: null,
        isAuthenticated: false,
        loading: false,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        portal: null,
        isAuthenticated: false,
        loading: false,
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const portal = localStorage.getItem('portal');
      
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          if (portal === 'parent') {
            const response = await api.get('/api/parent/me');
            if (response.data.success) {
              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: {
                  user: response.data.data,
                  token,
                  portal: 'parent',
                },
              });
              return;
            }
            throw new Error('parent session invalid');
          }

          const response = await api.get('/api/auth/me');
          
          if (response.data.success) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: response.data.user,
                token,
                portal: 'staff',
              },
            });
          } else {
            // Token is invalid
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('portal');
          delete api.defaults.headers.common['Authorization'];
          dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });

      if (response.data.success) {
        const { user, token, portal } = response.data;
        const nextPortal = portal === 'parent' ? 'parent' : 'staff';
        
        localStorage.setItem('token', token);
        localStorage.setItem('portal', nextPortal);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token, portal: nextPortal },
        });

        toast.success('Login successful!');
        if (nextPortal === 'parent') {
          navigate('/family');
        } else {
          const role = normalizeRole(user?.role);
          if (role === 'parent_feedback_agent') {
            navigate('/students');
          } else {
            navigate('/dashboard');
          }
        }
        
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Wrong credentials were entered. Please check your email and password.');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Wrong credentials were entered. Please check your email and password.';
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
      
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.post('/api/auth/register', userData);

      if (response.data.success) {
        const { user, token } = response.data;
        
        // Store token
        localStorage.setItem('token', token);
        
        // Set token in API headers
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token },
        });

        toast.success('Registration successful!');
        navigate('/dashboard');
        
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      toast.error(errorMessage);
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
      
      return { success: false, error: errorMessage };
    }
  };

  // Student registration function
  const registerStudent = async (studentData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.post('/api/auth/register/student', studentData);

      if (response.data.success) {
        const { user, token } = response.data;
        
        // Store token
        localStorage.setItem('token', token);
        
        // Set token in API headers
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token },
        });

        toast.success('Student registration successful!');
        navigate('/dashboard');
        
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Student registration failed');
      }
    } catch (error) {
      console.error('Student registration error:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Student registration failed';
      toast.error(errorMessage);
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE });
      
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('portal');
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  // Update user profile
  const updateUser = useCallback((userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData,
    });
  }, []);

  const updateProfile = async (profileData) => {
    try {
      const response = await api.patch('/api/auth/me', profileData);

      if (response.data?.success && response.data?.user) {
        updateUser(response.data.user);
        toast.success(response.data.message || 'Profile updated successfully');
        return { success: true, user: response.data.user };
      }

      throw new Error(response.data?.message || 'Profile update failed');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Profile update failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });

      if (response.data.success) {
        toast.success('Password changed successfully!');
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Password change failed');
      }
    } catch (error) {
      console.error('Change password error:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Password change failed';
      toast.error(errorMessage);
      
      return { success: false, error: errorMessage };
    }
  };

  const normalizeRole = (role) => (role == null ? '' : String(role).trim().toLowerCase());

  // Check if user has required role
  const hasRole = (requiredRoles) => {
    if (!state.user) return false;
    const userRole = normalizeRole(state.user.role);

    if (Array.isArray(requiredRoles)) {
      return requiredRoles.some((r) => userRole === normalizeRole(r));
    }

    return userRole === normalizeRole(requiredRoles);
  };

  // Check if user is admin
  const isAdmin = () => hasRole('admin');

  // Classroom teacher only (not supervisor)
  const isTeacher = () => hasRole('teacher');

  // Teacher, supervisor, or teaching admin (shared teacher UX / routing)
  const isTeacherOrSupervisor = () => hasRole(['teacher', 'teacher_supervisor', 'teacher_admin']);

  // Check if user is teacher manager/supervisor
  const isTeacherSupervisor = () => hasRole('teacher_supervisor');

  const isTeacherAdmin = () => hasRole('teacher_admin');

  /** Team supervisor or global teaching admin */
  const isTeachingLead = () => hasRole(['teacher_supervisor', 'teacher_admin']);

  const hasGlobalTeacherOps = () => hasRole(['admin', 'teacher_admin']);

  // Check if user is student
  const isStudent = () => hasRole('student');

  // Sales user or sales supervisor (matches backend requireSales / requireSalesOrAdmin)
  const isSales = () => hasRole(['sales', 'sales_supervisor', 'country_manager']);

  const isSalesSupervisor = () => hasRole('sales_supervisor');

  const isCountryManager = () => hasRole('country_manager');

  /** sales_supervisor and country_manager share Assigned-country sales access. */
  const isSalesCountryScope = () => hasRole(['sales_supervisor', 'country_manager']);

  const isParentFeedbackAgent = () => hasRole('parent_feedback_agent');

  const isBatchOps = () => hasRole(['batch_supervisor', 'batch_admin']);

  /** Assigned countries for country-scoped roles (sales_supervisor / parent_feedback_agent). */
  const getAssignedCountries = () => {
    const list = state.user?.assignedCountries || state.user?.assigned_countries || [];
    return Array.isArray(list) ? list.filter(Boolean) : [];
  };

  /**
   * sales_supervisor and country_manager must have at least one assigned country.
   * Other roles always return true.
   */
  const hasSalesSupervisorCountryAccess = () => {
    if (!isSalesCountryScope()) return true;
    return getAssignedCountries().length > 0;
  };

  const canApproveLeave = () => hasRole(['admin', 'teacher_admin', 'teacher_supervisor']);

  const canManageLeaveCoverage = () =>
    hasRole(['admin', 'teacher_admin', 'teacher_supervisor', 'country_manager']);

  /** Batch create/edit, schedules, pause — teaching lead + country manager + batch ops */
  const canManageBatches = () =>
    hasRole(['admin', 'teacher_admin', 'teacher_supervisor', 'country_manager', 'batch_supervisor', 'batch_admin']);

  /** Teacher profiles (name, contact, zoom, availability) — not user-role admin */
  const canManageTeachers = () =>
    hasRole(['admin', 'teacher_admin', 'teacher_supervisor', 'country_manager']);

  // Finance roles (same as backend requireFinanceOrAdmin)
  const FINANCE_ROLES = ['admin', 'finance_user', 'finance_supervisor', 'finance_admin'];
  const isFinanceOrAdmin = () => (state.user && FINANCE_ROLES.includes(state.user.role));
  const isFinanceSupervisor = () => hasRole('finance_supervisor');

  /** Student profile fees/discount/currency — keep in sync with studentFinancialAccess.js */
  const canViewStudentFinancials = () =>
    hasRole([
      'admin',
      'sales',
      'sales_supervisor',
      'country_manager',
      'finance_user',
      'finance_supervisor',
      'finance_admin',
    ]);

  const isFamilyPortal = () => state.portal === 'parent';

  const value = {
    user: state.user,
    token: state.token,
    portal: state.portal,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    login,
    register,
    registerStudent,
    logout,
    updateUser,
    updateProfile,
    changePassword,
    hasRole,
    isAdmin,
    isTeacher,
    isTeacherOrSupervisor,
    isTeacherSupervisor,
    isTeacherAdmin,
    isTeachingLead,
    hasGlobalTeacherOps,
    isStudent,
    isSales,
    isSalesSupervisor,
    isCountryManager,
    isSalesCountryScope,
    isParentFeedbackAgent,
    isBatchOps,
    getAssignedCountries,
    hasSalesSupervisorCountryAccess,
    canApproveLeave,
    canManageLeaveCoverage,
    canManageBatches,
    canManageTeachers,
    isFinanceOrAdmin,
    isFinanceSupervisor,
    canViewStudentFinancials,
    isFamilyPortal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext; 